import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api-client';
import { useAuth } from '../../lib/auth';
import { emailKey } from '../../lib/email-api';
import { resourceApi, type AutomationDef, type Condition, type MessageSpec, type Segment, type TemplateDef } from '../../lib/email-resources-api';
import type { SchemaDescriptor } from '../../lib/api-types';
import { MarkdownContent } from '../content/MarkdownContent';
import { NoteEditor } from '../notes/NoteEditor';
import { MailSurface, MailSubject } from '../email/MailSurface';
import { SequenceChatEditor, TemplateEditButton } from '../email/EmailWorkspace';
import type { ArtifactReference } from './artifact-reference';
import styles from './ArtifactCard.module.css';

export function ArtifactCard({ reference }: { reference: ArtifactReference }) {
  return reference.kind === 'note' ? <NoteCard id={reference.id} /> :
    <EmailResourceCard id={reference.id} kind={reference.kind === 'template' ? 'templates' : 'automations'} />;
}

function Unavailable({ error, retry }: { error: Error | null; retry: () => void }) {
  const missing = error instanceof ApiError && error.status === 404;
  return <p role="status" className={styles.unavailable}>{missing ? 'This saved item was deleted or is no longer available.' : 'Couldn’t load this saved item.'}
    {!missing && <button type="button" onClick={retry}>Try again</button>}</p>;
}

function DeleteControl({ label, busy, onDelete }: { label: string; busy: boolean; onDelete: () => void }) {
  const [confirm, setConfirm] = useState(false);
  return confirm ? <span className={styles.confirm}>
    <span>Delete this saved {label}? This removes the item, not the chat message.</span>
    <button type="button" disabled={busy} onClick={onDelete}>Delete {label}</button>
    <button type="button" disabled={busy} onClick={() => setConfirm(false)}>Cancel</button>
  </span> : <button type="button" onClick={() => setConfirm(true)}>Delete</button>;
}

function NoteCard({ id }: { id: string }) {
  const { bootstrap } = useAuth();
  const scope = `${bootstrap?.workspace.id}:${bootstrap?.user?.id}`;
  const key = ['records', 'artifact', scope, id];
  const cache = useQueryClient();
  const query = useQuery({ queryKey: key, queryFn: () => api.getRecord('note', id), retry: false });
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState<{ title: string; body: string; baseline: string }>();
  const [deleted, setDeleted] = useState(false);
  const save = useMutation({ mutationFn: async () => {
    // Do not overwrite an Echo edit that arrived while the editor was open.
    const fresh = await api.getRecord('note', id);
    if (fresh.record.updatedAt !== draft!.baseline) throw new Error('This note changed while you were editing. Your draft is still here; reopen the note to compare before saving.');
    return api.updateRecord('note', id, { title: draft!.title, body: draft!.body });
  }, onSuccess: async () => { setDraft(undefined); await Promise.all(['records', 'record', 'timeline'].map(k => cache.invalidateQueries({ queryKey: [k] }))); } });
  const remove = useMutation({ mutationFn: () => api.deleteRecord('note', id), onSuccess: async () => {
    setDeleted(true);
    await Promise.all(['records', 'record', 'schema', 'timeline'].map(k => cache.invalidateQueries({ queryKey: [k] })));
  } });
  if (deleted) return <article className={styles.card} aria-label="Deleted note">Note deleted.</article>;
  if (query.isLoading) return <p role="status">Loading saved note…</p>;
  if (query.isError || !query.data) return <Unavailable error={query.error} retry={() => void query.refetch()} />;
  const { record, object } = query.data;
  const title = typeof record.data.title === 'string' ? record.data.title : record.displayName;
  const body = typeof record.data.body === 'string' ? record.data.body : '';
  const link = `/app/o/${encodeURIComponent(object.namePlural)}/${encodeURIComponent(id)}`;
  return <article className={styles.card} aria-label="Saved note">
    <header className={styles.header}><span>Note · Saved</span><a href={link}>Open note ↗</a></header>
    {draft ? <form className={styles.editor} onSubmit={event => { event.preventDefault(); save.mutate(); }}>
      <label>Title<input value={draft.title} disabled={save.isPending} onChange={e => setDraft({ ...draft, title: e.target.value })} required /></label>
      <NoteEditor value={draft.body} onChange={body => setDraft({ ...draft, body })} disabled={save.isPending} />
      <div className={styles.actions}><button type="submit" disabled={save.isPending}>Save note</button><button type="button" disabled={save.isPending} onClick={() => { setDraft(undefined); save.reset(); }}>Cancel</button></div>
    </form> : <>
      <h3>{title}</h3>
      <div className={styles.document} data-expanded={expanded}><MarkdownContent source={body.replace(/^\s*# [^\n]+\n*/, '') || 'This note has no body yet.'} /></div>
      <div className={styles.actions}>
        <button type="button" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>{expanded ? 'Collapse' : 'Read full note'}</button>
        <button type="button" onClick={() => { save.reset(); setDraft({ title, body, baseline: record.updatedAt }); }}>Edit</button>
        <DeleteControl label="note" busy={remove.isPending} onDelete={() => remove.mutate()} />
      </div>
    </>}
    {(save.error || remove.error) && <p role="alert">{(save.error || remove.error)?.message}</p>}
  </article>;
}

export function fieldLabel(schema: SchemaDescriptor | undefined, fieldId: string) {
  return schema?.objects.flatMap(object => object.fields).find(field => field.id === fieldId)?.label ?? 'Saved field';
}
export function SegmentText({ segments, schema }: { segments: Segment[]; schema?: SchemaDescriptor | undefined }) {
  return <>{segments.map((segment, i) => segment.kind === 'literal' ? segment.text :
    <span className={styles.token} key={i} title={`${segment.source === 'recipient' ? 'Contact' : 'Record'} detail${segment.fallback ? ` · fallback: ${segment.fallback}` : ''}`}>{fieldLabel(schema, segment.fieldId)}</span>)}</>;
}
function EmailMessagePreview({ message, schema }: { message: MessageSpec; schema?: SchemaDescriptor | undefined }) {
  if (message.mode === 'guidance') return <div className={styles.emailBody}><strong>Writing guidance: {message.purpose}</strong><p>{message.instructions}</p>{message.example && <><strong>Example wording</strong><p>{message.example}</p></>}</div>;
  return <><div className={styles.subject}><strong>Subject: </strong><SegmentText segments={message.subject} schema={schema} /></div>
    <div className={styles.emailBody}><SegmentText segments={message.body} schema={schema} /></div></>;
}
function Conditions({ conditions, schema }: { conditions: Condition[]; schema?: SchemaDescriptor | undefined }) {
  return <>{conditions.map((c, i) => <span key={i}>{i > 0 ? '; ' : ''}{fieldLabel(schema, c.fieldId)} {c.operator.replaceAll('_', ' ')}{c.value !== undefined ? ` ${c.value}` : ''}</span>)}</>;
}

function EmailResourceCard({ id, kind }: { id: string; kind: 'templates' | 'automations' }) {
  const [editing, setEditing] = useState(false);
  const cache = useQueryClient();
  const key = [...emailKey(), kind, id];
  const query = useQuery({ queryKey: key, queryFn: () => resourceApi.detail(kind, id), retry: false });
  const schema = useQuery({ queryKey: ['schema'], queryFn: api.schema });
  const [expanded, setExpanded] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const remove = useMutation({ mutationFn: () => resourceApi.remove(kind, id, query.data!.revision), onSuccess: async () => {
    setDeleted(true); await cache.invalidateQueries({ queryKey: emailKey() });
  } });
  const label = kind === 'templates' ? 'template' : 'sequence';
  if (deleted || query.data?.state === 'deleted') return <article className={styles.card} aria-label={`Deleted ${label}`}>{label === 'template' ? 'Template' : 'Sequence'} deleted.</article>;
  if (query.isLoading) return <p role="status">Loading saved {label}…</p>;
  if (query.isError || !query.data) return <Unavailable error={query.error} retry={() => void query.refetch()} />;
  const resource = query.data;
  if (kind === 'automations' && editing) return <SequenceChatEditor resource={resource}
    onSaved={() => void query.refetch()} onClose={() => setEditing(false)} />;
  const link = `/app/email/${kind}/${encodeURIComponent(id)}`;
  const footer = <div className={styles.actions}>
    <button type="button" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>{expanded ? 'Collapse' : `Read full ${label}`}</button>
    {kind === 'automations' ? <button type="button" onClick={() => setEditing(true)}>Edit sequence</button>
      : schema.data ? <TemplateEditButton objects={schema.data.objects} template={resource}>Edit template</TemplateEditButton> : <a href={link}>Edit {label}</a>}
    <DeleteControl label={label} busy={remove.isPending} onDelete={() => remove.mutate()} />
    {remove.error && <p role="alert">{remove.error.message}</p>}
  </div>;
  const heading = <header className={styles.header}><span>{kind === 'templates' ? 'Email template · Saved' : `Sequence · ${resource.state ?? 'draft'}`} · v{resource.revision}</span><a href={link}>Open {label} ↗</a></header>;
  if (kind === 'templates') return <div className={styles.emailCard}>
    <MailSurface label="Saved email template">{heading}<MailSubject subject={resource.name} />
      <div className={styles.document} data-expanded={expanded}><EmailMessagePreview message={(resource.definition as TemplateDef).message} schema={schema.data} /></div>
      <p className={styles.caption}>Reusable wording · personalized when applied to a contact</p>{footer}
    </MailSurface>
  </div>;
  const definition = resource.definition as AutomationDef;
  return <article className={styles.card} aria-label="Saved sequence">
    {heading}<h3>{resource.name}</h3>
    <p className={styles.caption}>{resource.state === 'active' ? 'Active sequence · Showing saved working copy; existing runs use their published version.' : 'Not active.'} Each email requires send approval.</p>
    <div>
      <p><strong>Starts: </strong>{definition.trigger.kind === 'field_changed' ? `${fieldLabel(schema.data, definition.trigger.fieldId)} changes to ${definition.trigger.to}` : definition.trigger.kind === 'manual' ? 'When you enroll contacts' : 'When a record is created'}</p>
      {definition.conditions.length > 0 && <p><strong>Only when: </strong><Conditions conditions={definition.conditions} schema={schema.data} /></p>}
      <ol className={styles.sequence}>{definition.steps.map((step, i) => {
        const resolved = resource.stepMessages?.find(item => item.stepId === step.id);
        const message = 'inline' in step.message ? step.message.inline : resolved?.message;
        return <li key={step.id}>
          <p className={styles.caption}>{i === 0 ? 'First email' : step.afterPreviousSend ? `Wait ${step.afterPreviousSend.amount} ${step.afterPreviousSend.unit.replaceAll('_', ' ')} after the previous send` : 'Follow-up timing not set'}</p>
          <strong>Email {i + 1}{step.threadMode === 'reply_previous' ? ' · Reply in the same conversation' : ''}</strong>
          {message ? expanded ? <EmailMessagePreview message={message} schema={schema.data} /> :
            <p className={styles.stepSummary}>{message.mode === 'guidance' ? `Writing guidance: ${message.purpose}` : message.subject.length ? <SegmentText segments={message.subject} schema={schema.data} /> : 'Continues the previous email'}</p>
            : <p>{resolved?.error ?? 'Saved template version — open the sequence to review.'}</p>}
        </li>;
      })}</ol>
      <p><strong>Stops: </strong>when they reply.{definition.stopConditions.length > 0 && <> Also when: <Conditions conditions={definition.stopConditions} schema={schema.data} />.</>}</p>
    </div>{footer}
  </article>;
}
